// API para el módulo de Hojas de Gastos
// Centraliza todos los endpoints relacionados con expense sheets

import type { AxiosError, AxiosRequestConfig } from "axios";
import discovery from "../../discovery";
import { HTTP, UTILS } from "../../http-common";
import { worktimeApi } from "../worktime";
import type {
  ExpenseSheet,
  ExpenseSheetCategory,
  ExpenseSheetItem,
  ExpenseSheetItemListParams,
  ExpenseSheetListParams,
  FullExpenseSheet,
} from "./expenseSheetsDTO";

/**
 * Callbacks genéricos
 */
type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

/**
 * API cliente para Hojas de Gastos
 */
export class ExpenseSheetsApi {
  /**
   * Obtiene las hojas de gastos de una empresa
   */
  getExpenseSheets(
    params: ExpenseSheetListParams,
    onSuccess: SuccessCallback<ExpenseSheet[]>,
    onError: ErrorCallback
  ): void {
    const { companyId, sortBy = "id", sortDesc = false } = params;
    worktimeApi.genericGet(
      `user/expense-sheet/${companyId}/list?sortBy=${sortBy}&sortDesc=${sortDesc}`,
      onSuccess,
      onError
    );
  }

  /**
   * Obtiene una hoja de gastos completa por ID
   */
  getFullExpenseSheetById(
    id: number,
    onSuccess: SuccessCallback<FullExpenseSheet>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/full-expense-sheet/${id}`, onSuccess, onError);
  }

  /**
   * Obtiene una hoja de gastos por ID
   */
  getExpenseSheetById(
    id: number,
    onSuccess: SuccessCallback<ExpenseSheet>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/expense-sheet/${id}`, onSuccess, onError);
  }

  /**
   * Guarda una hoja de gastos
   */
  storeExpenseSheet(
    data: ExpenseSheet,
    onSuccess: SuccessCallback<ExpenseSheet>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost("user/expense-sheet", data, onSuccess, onError);
  }

  /**
   * Envía una hoja de gastos por ID
   */
  sendExpenseSheetById(
    id: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost(
      `user/expense-sheet/${id}/send`,
      null,
      onSuccess,
      onError
    );
  }

  /**
   * Cancela una solicitud de hoja de gastos por ID
   */
  cancelExpenseSheetRequestById(
    id: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost(
      `user/expense-sheet/${id}/cancel-request`,
      null,
      onSuccess,
      onError
    );
  }

  /**
   * Elimina una hoja de gastos por ID
   */
  deleteExpenseSheetById(
    id: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericDelete(`user/expense-sheet/${id}`, onSuccess, onError);
  }

  /**
   * Obtiene los items de una hoja de gastos por ID de hoja
   */
  getExpenseSheetItemsBySheetId(
    params: ExpenseSheetItemListParams,
    onSuccess: SuccessCallback<ExpenseSheetItem[]>,
    onError: ErrorCallback
  ): void {
    const { sheetId, sortBy = "id", sortDesc = false } = params;
    worktimeApi.genericGet(
      `user/expense-sheet-item/${sheetId}/list?sortBy=${sortBy}&sortDesc=${sortDesc}`,
      onSuccess,
      onError
    );
  }

  /**
   * Obtiene un item de hoja de gastos por ID
   */
  getExpenseSheetItemById(
    id: number,
    onSuccess: SuccessCallback<ExpenseSheetItem>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(`user/expense-sheet-item/${id}`, onSuccess, onError);
  }

  /**
   * Guarda un item de hoja de gastos
   */
  storeExpenseSheetItem(
    data: unknown,
    onSuccess: SuccessCallback<ExpenseSheetItem>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost(
      "user/expense-sheet-item",
      data,
      onSuccess,
      onError
    );
  }

  /**
   * Elimina un item de hoja de gastos por ID
   */
  deleteExpenseSheetItemById(
    id: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericDelete(
      `user/expense-sheet-item/${id}`,
      onSuccess,
      onError
    );
  }

  /**
   * Extrae información del ticket de un item de hoja de gastos
   */
  extractExpenseSheetItemTicketInfo(
    data: unknown,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost(
      "user/expense-sheet-item/ticket-info",
      data,
      onSuccess,
      onError
    );
  }

  /**
   * Elimina el documento de un item de hoja de gastos
   */
  deleteExpenseSheetItemDocument(
    id: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost(
      `user/expense-sheet-item/${id}/delete-document`,
      null,
      onSuccess,
      onError
    );
  }

  /**
   * Descarga el contenido de un documento de un item de hoja de gastos como Base64
   * Con detección mejorada de MIME types (incluyendo HEIC)
   */
  downloadExpenseSheetItemDocumentContent(
    sheetId: number,
    sheetItemId: number,
    onSuccess: SuccessCallback<string>,
    onError: ErrorCallback
  ): void {
    const options: AxiosRequestConfig = {
      ...worktimeApi["getCommonHeaders"](),
      responseType: "blob",
    };

    HTTP.get(
      `${discovery.worktime_service_url}user/expense-sheet-item/${sheetId}/download/${sheetItemId}`,
      options
    )
      .then(async (response) => {
        if (onSuccess) {
          const blob = response.data;

          // Convertir blob a ArrayBuffer y luego a Uint8Array para navegador
          const arrayBuffer = await blob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);

          // Asegurar MIME type correcto para mejor compatibilidad (ej: Android 12 WebView)
          let mime = blob.type || "";

          // Función para detectar MIME de imagen por magic numbers
          const detectImageMime = (buf: Uint8Array): string | null => {
            if (!buf || buf.length < 12) return null;
            // JPEG FF D8 FF
            if (buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff)
              return "image/jpeg";
            // PNG 89 50 4E 47
            if (
              buf[0] === 0x89 &&
              buf[1] === 0x50 &&
              buf[2] === 0x4e &&
              buf[3] === 0x47
            )
              return "image/png";
            // GIF 47 49 46 38
            if (
              buf[0] === 0x47 &&
              buf[1] === 0x49 &&
              buf[2] === 0x46 &&
              buf[3] === 0x38
            )
              return "image/gif";
            // WEBP RIFF....WEBP
            if (
              buf[0] === 0x52 &&
              buf[1] === 0x49 &&
              buf[2] === 0x46 &&
              buf[3] === 0x46 &&
              buf[8] === 0x57 &&
              buf[9] === 0x45 &&
              buf[10] === 0x42 &&
              buf[11] === 0x50
            )
              return "image/webp";
            // HEIC brand inside ftyp box
            if (
              buf[4] === 0x66 &&
              buf[5] === 0x74 &&
              buf[6] === 0x79 &&
              buf[7] === 0x70 &&
              // heic | heix | hevc | hevx
              ((buf[8] === 0x68 &&
                buf[9] === 0x65 &&
                buf[10] === 0x69 &&
                buf[11] === 0x63) ||
                (buf[8] === 0x68 &&
                  buf[9] === 0x65 &&
                  buf[10] === 0x69 &&
                  buf[11] === 0x78) ||
                (buf[8] === 0x68 &&
                  buf[9] === 0x65 &&
                  buf[10] === 0x76 &&
                  buf[11] === 0x63) ||
                (buf[8] === 0x68 &&
                  buf[9] === 0x65 &&
                  buf[10] === 0x76 &&
                  buf[11] === 0x78))
            )
              return "image/heic";
            return null;
          };

          if (!mime || !mime.startsWith("image/")) {
            const detected = detectImageMime(uint8Array);
            if (detected) {
              mime = detected;
            }
          }

          // Fallback final a JPEG
          if (!mime || !mime.startsWith("image/")) {
            mime = "image/jpeg";
          }

          // Convertir Uint8Array a base64 usando FileReader (compatible con navegador)
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            // Asegurar que tenga el data URI correcto
            if (base64.startsWith("data:")) {
              // Reemplazar el mime type si es necesario
              const base64WithCorrectMime = base64.replace(
                /^data:[^;]+/,
                `data:${mime}`
              );
              onSuccess(base64WithCorrectMime);
            } else {
              onSuccess(`data:${mime};base64,${base64}`);
            }
          };
          reader.onerror = () => {
            if (onError) {
              const error = new Error("Error al convertir blob a base64");
              onError(error as AxiosError);
            }
          };
          reader.readAsDataURL(blob);
        }
      })
      .catch((error) => {
        if (onError) {
          onError(error);
        }
      });
  }

  /**
   * Descarga el documento de un item de hoja de gastos
   */
  downloadExpenseSheetItemDocument(
    sheetId: number,
    sheetItemId: number,
    onSuccess?: SuccessCallback,
    onError?: ErrorCallback
  ): void {
    const options: AxiosRequestConfig = {
      ...worktimeApi["getCommonHeaders"](),
      responseType: "blob",
    };

    HTTP.get(
      `${discovery.worktime_service_url}user/expense-sheet-item/${sheetId}/download/${sheetItemId}`,
      options
    )
      .then((response) => {
        if (onSuccess) {
          onSuccess(response.data);
        }
        UTILS.downloadDocument(response);
      })
      .catch((error) => {
        if (onError) {
          onError(error);
        }
      });
  }

  /**
   * Obtiene las categorías de hojas de gastos
   */
  getExpenseSheetCategories(
    onSuccess: SuccessCallback<ExpenseSheetCategory[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet("user/expense-sheet-category", onSuccess, onError);
  }
}

// Exportar instancia única (singleton)
export const expenseSheetsApi = new ExpenseSheetsApi();

// Exportar como default para compatibilidad
export default expenseSheetsApi;
